import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { portfolioAlt } from '../lib/portfolioAlt';

export default function Lightbox({ images, index, onClose, onPrev, onNext }) {
  const dialogRef = React.useRef(null);
  React.useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector('button')?.focus();
    return () => { document.body.style.overflow = overflow; previous?.focus(); };
  }, []);
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Tab') {
        const buttons = dialogRef.current?.querySelectorAll('button');
        if (!buttons?.length) return;
        const target = e.shiftKey ? buttons[buttons.length - 1] : buttons[0];
        if (document.activeElement === (e.shiftKey ? buttons[0] : buttons[buttons.length - 1])) { e.preventDefault(); target.focus(); }
      }
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  if (index === null || index === undefined) return null;
  const img = images[index];

  return (
    <AnimatePresence>
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Photographie du portfolio"
        data-testid="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <button data-testid="lightbox-close" aria-label="Fermer la photographie" onClick={(e)=>{e.stopPropagation(); onClose();}} className="absolute top-6 right-6 text-text-cream hover:text-accent-light z-10">
          <X size={28} strokeWidth={1.2}/>
        </button>
        <button data-testid="lightbox-prev" aria-label="Photographie précédente" onClick={(e)=>{e.stopPropagation(); onPrev();}} className="absolute left-6 top-1/2 -translate-y-1/2 text-text-cream hover:text-accent-light z-10">
          <ChevronLeft size={40} strokeWidth={1.2}/>
        </button>
        <button data-testid="lightbox-next" aria-label="Photographie suivante" onClick={(e)=>{e.stopPropagation(); onNext();}} className="absolute right-6 top-1/2 -translate-y-1/2 text-text-cream hover:text-accent-light z-10">
          <ChevronRight size={40} strokeWidth={1.2}/>
        </button>
        <motion.img
          key={img.id}
          src={img.url}
          alt={portfolioAlt(img)}
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="max-h-[88vh] max-w-[90vw] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </motion.div>
    </AnimatePresence>
  );
}
